import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type { OfferPageState, OfferPageDispatchAction } from 'contexts/OfferPage/offerPageStateReducer'
import { INITIAL_OFFER_PAGE_STATE, OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { Experiments } from 'constants/optimizely'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { memo, useEffect, useMemo, useState } from 'react'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import accommodationMobileFilterMiddleware from 'components/OfferPage/Accommodation/AccommodationMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddleware } from '../utils'
import FlashHotelContent from './FlashHotelContent'
import {
  getFirstAvailableDuration,
  getFlashCalendarSkipDurationOptions,
  shouldSkipFlashDurationStep,
} from './flashHotelCalendarSkipUtils'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import useMountedEffect from 'hooks/useMountedEffect'
import { offerPageDurationChangeEvent, offerPageGuestSelectionEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import multiRoomsMobileFilterMiddleware from '../MultipleRoomsBooking/Mobile/MobileMultipleRoomsFilterMiddleware'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [accommodationMobileFilterMiddleware]
const mobileNewMultiRoomMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [multiRoomsMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]
const defaultRooms = [generateOfferPageRoom()]

interface Props {
  offer: App.HotelOffer
}

function FlashHotelContainer(props: Props) {
  const { offer } = props
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const defaults = useAppSelector(state => getInitialLeOfferPageState(state, offer))

  // This size MUST be kept in sync with the size prop for rendering `FlashHotelSidebarFilters`
  const isMobileSize = useScreenSizeOnly('mobile')
  const { isAnyVariant: isFlashCalendarSkipEnabled } = useExperiment(
    Experiments.mobileWebOfferPageFlashCalendarSkip,
    { canActivate: isMobileSize },
  )
  const defaultOfferDuration = offer.lowestPricePackage?.duration
  const offerStateForDurationOptions = useMemo(() => ({
    ...INITIAL_OFFER_PAGE_STATE,
    ...defaults,
    duration: defaults.duration ?? defaultOfferDuration ?? 0,
    offer,
  }), [defaults, defaultOfferDuration, offer])
  const durationOptions = useAppSelector(appState =>
    getFlashCalendarSkipDurationOptions(offerStateForDurationOptions, appState),
  )
  const firstAvailableDuration = useMemo(
    () => getFirstAvailableDuration(durationOptions),
    [durationOptions],
  )
  const entryDuration = defaults.duration ?? defaultOfferDuration
  const shouldPreselectDuration = shouldSkipFlashDurationStep({
    isExperimentEnabled: isMobileSize && isFlashCalendarSkipEnabled,
    durationSelected: defaults.durationSelected,
    currentDuration: entryDuration,
    defaultDuration: defaultOfferDuration,
    firstAvailableDuration,
  })
  const effectiveDuration = defaults.duration ?? (shouldPreselectDuration ? firstAvailableDuration : defaultOfferDuration)

  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: effectiveDuration,
    durationSelected: defaults.durationSelected || shouldPreselectDuration,
    offer,
  }, middlewares)

  useEffect(() => {
    if (!shouldPreselectDuration || firstAvailableDuration == null || state.durationSelected) {
      return
    }

    stateDispatch({ type: OfferPageActions.setDuration, duration: firstAvailableDuration })
  }, [firstAvailableDuration, shouldPreselectDuration, state.durationSelected, stateDispatch])

  const isMultiIndividualRoom = useAppSelector(appState => isMultipleRoomBookingActive(state, appState))

  useSyncHotelTripItem(state, offer)

  useEffect(() => {
    if (isMobileSize) {
      if (isMultiIndividualRoom) {
        setMiddlewares(mobileNewMultiRoomMiddlewares)
      } else {
        setMiddlewares(mobileVariantMiddlewares)
      }
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [isMobileSize, isMultiIndividualRoom])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useMountedEffect(() => {
    trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <FlashHotelContent
      offer={offer}
    />
  </OfferPageStateProvider>
}

export default memo(FlashHotelContainer)
